import type { Theme } from '@mui/material';
import type { Components } from 'react-markdown';

import React from 'react';
import remarkGfm from 'remark-gfm';
import ReactMarkdown from 'react-markdown';

import { Box, Link, Table, TableRow, TableBody, TableCell, TableHead, Typography } from '@mui/material';

const REMARK_PLUGINS = [remarkGfm];

// Set on each Typography, not on an ancestor: Typography declares its own
// body1 size, which beats any inherited one.
const proseSx = (theme: Theme) => ({
  fontSize: { xs: theme.typography.pxToRem(17), md: theme.typography.pxToRem(15) },
  lineHeight: 1.55,
});

function Heading({ children }: { children?: React.ReactNode }): React.JSX.Element {
  return (
    <Typography
      component="p"
      sx={(theme) => ({
        ...proseSx(theme),
        mt: 2,
        mb: 0.5,
        fontWeight: theme.typography.fontWeightSemiBold,
        '&:first-of-type': { mt: 0 },
      })}
    >
      {children}
    </Typography>
  );
}

// No rehype-raw: the body is model-authored, so raw HTML must stay inert text.
const COMPONENTS: Components = {
  // The glasses have no pointer, so a hover-only underline never shows.
  // react-markdown blanks an unsafe protocol, and href="" reloads the app.
  a: ({ href, children }) =>
    href ? (
      <Link
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        underline="always"
        color="inherit"
        sx={{ textUnderlineOffset: '0.2em' }}
      >
        {children}
      </Link>
    ) : (
      <>{children}</>
    ),
  p: ({ children }) => (
    <Typography component="p" sx={(theme) => ({ ...proseSx(theme), my: 1, '&:first-of-type': { mt: 0 } })}>
      {children}
    </Typography>
  ),
  h1: ({ children }) => <Heading>{children}</Heading>,
  h2: ({ children }) => <Heading>{children}</Heading>,
  h3: ({ children }) => <Heading>{children}</Heading>,
  h4: ({ children }) => <Heading>{children}</Heading>,
  h5: ({ children }) => <Heading>{children}</Heading>,
  h6: ({ children }) => <Heading>{children}</Heading>,
  ul: ({ children }) => (
    <Box component="ul" sx={{ my: 1, pl: 3 }}>
      {children}
    </Box>
  ),
  ol: ({ children }) => (
    <Box component="ol" sx={{ my: 1, pl: 3 }}>
      {children}
    </Box>
  ),
  li: ({ children }) => (
    <Typography component="li" sx={(theme) => ({ ...proseSx(theme), my: 0.5 })}>
      {children}
    </Typography>
  ),
  blockquote: ({ children }) => (
    <Box
      component="blockquote"
      sx={{ my: 1, ml: 0, pl: 2, borderLeft: 2, borderColor: 'divider', color: 'text.secondary' }}
    >
      {children}
    </Box>
  ),
  code: ({ children }) => (
    <Box
      component="code"
      sx={(theme) => ({
        px: 0.5,
        borderRadius: 0.5,
        bgcolor: theme.palette.container?.low ?? theme.palette.action.hover,
        fontFamily: 'monospace',
        fontSize: '0.9em',
        wordBreak: 'break-word',
      })}
    >
      {children}
    </Box>
  ),
  pre: ({ children }) => (
    <Box
      component="pre"
      sx={(theme) => ({
        my: 1,
        p: 1.5,
        borderRadius: 1,
        bgcolor: theme.palette.container?.low ?? theme.palette.action.hover,
        overflowX: 'auto',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      })}
    >
      {children}
    </Box>
  ),
  table: ({ children }) => (
    <Table size="small" sx={{ my: 1.5, width: 'auto' }}>
      {children}
    </Table>
  ),
  thead: ({ children }) => <TableHead>{children}</TableHead>,
  tbody: ({ children }) => <TableBody>{children}</TableBody>,
  tr: ({ children }) => <TableRow>{children}</TableRow>,
  th: ({ children }) => (
    <TableCell component="th" sx={(theme) => ({ ...proseSx(theme), fontWeight: theme.typography.fontWeightSemiBold })}>
      {children}
    </TableCell>
  ),
  td: ({ children }) => <TableCell sx={proseSx}>{children}</TableCell>,
  hr: () => <Box component="hr" sx={{ my: 2, border: 0, borderTop: 1, borderColor: 'divider' }} />,
  img: () => null,
};

export interface CopilotMarkdownProps {
  children: string;
}

// Memoized: react-markdown re-parses the whole body on every render.
export const CopilotMarkdown = React.memo(function CopilotMarkdown({
  children,
}: CopilotMarkdownProps): React.JSX.Element {
  return (
    <Box data-testid="ari.bot-ui.copilot-markdown" sx={{ '& > *:last-child': { mb: 0 } }}>
      <ReactMarkdown remarkPlugins={REMARK_PLUGINS} components={COMPONENTS}>
        {children}
      </ReactMarkdown>
    </Box>
  );
});
